import { BoundingRect, getBoundingRectFromBufferGeometry } from "../BoundingRect";
import { MeshStats } from "../modelConfig/MeshStats";
import { RawMesh } from "../rawModel/RawMesh";
import { BufferGeometry } from "three";
import { createBufferGeometry } from "../createBufferGeometry";
import { MeshConfig } from "../modelConfig/MeshConfig";
import { v4 as uuidv4 } from "uuid";
import { ModelConfig } from "../modelConfig/ModelConfig";

export type MeshInstance = {
    id: string;
    raw: RawMesh;
    config: MeshConfig;
    stats: MeshStats;
    geometry: BufferGeometry;
    isHidden: boolean;
    box: BoundingRect;
    groupId?: string;
};

export const createMeshInstance = (
    mesh: RawMesh,
    config: MeshConfig,
    stats: MeshStats,
    sourceBoundaries: BoundingRect,
    targetBoundaries?: BoundingRect,
    skipUvResize?: boolean
): MeshInstance => {
    const geometry = targetBoundaries
        ? createBufferGeometry(
              mesh,
              stats,
              sourceBoundaries.center,
              sourceBoundaries,
              targetBoundaries,
              skipUvResize
          )
        : mesh.getBufferGeometry();

    const box = getBoundingRectFromBufferGeometry(geometry);

    return {
        id: uuidv4(),
        raw: mesh,
        config,
        stats,
        geometry,
        isHidden: config.isHidden || config.isArea,
        box,
    };
};

export const createMeshInstances = (
    meshes: ReadonlyArray<RawMesh>,
    config: ModelConfig,
    meshStatsProvider: (meshName: string) => MeshStats,
    sourceBoundaries: BoundingRect,
    targetBoundaries?: BoundingRect,
    skipUvResize?: boolean
): Record<string, MeshInstance> => {
    const meshesInstances: Record<string, MeshInstance> = {};

    for (const mesh of meshes) {
        const meshName = mesh.name;

        const instance = createMeshInstance(
            mesh,
            config.meshConfig[meshName],
            meshStatsProvider(meshName),
            sourceBoundaries,
            targetBoundaries,
            skipUvResize
        );

        meshesInstances[meshName] = instance;
    }

    return meshesInstances;
};
